import { FC } from 'react';
import { css } from '@emotion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { spacing, colors, fontStyles } from '../../../styles';
import { cleanMarkdownContent } from '../../../utils/contentCleaning';
import { ToolRendererProps } from './types';

// Trades Analysis Renderer
export const TradesRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  if (isLoading) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Analyzing trades...
      </div>
    );
  }

  const analysis = output.analysis || output.content || output.summary;
  const metrics = output.metrics || output.stats;
  const trades = output.trades || output.data;

  // If we have markdown content, render it with full styling (matching CatalystRenderer)
  if (
    analysis &&
    analysis.length > 100 &&
    (analysis.includes('## ') || analysis.includes('### ') || analysis.includes('|'))
  ) {
    return (
      <div
        css={css`
          color: ${colors.liquorice};
          font-size: 14px;
          line-height: 1.4;

          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }

          /* Markdown styles matching ChatPage */
          & p {
            margin: 0 0 ${spacing.centi} 0;
            &:last-child {
              margin-bottom: 0;
            }
          }
          & h1 {
            ${fontStyles.lion}
            margin: 0 0 ${spacing.centi} 0;
          }
          & h2 {
            ${fontStyles.dog}
            margin: 0 0 ${spacing.centi} 0;
          }
          & h3 {
            font-size: 16px;
            font-weight: 600;
            margin: 0 0 ${spacing.milli} 0;
          }
          & ul,
          & ol {
            margin: ${spacing.centi} 0;
            padding-left: ${spacing.regular};
          }
          & li {
            margin: ${spacing.milli} 0;
          }
          & code {
            background-color: rgba(0, 0, 0, 0.1);
            padding: ${spacing.micro} ${spacing.milli};
            border-radius: 4px;
          }
          & pre {
            background-color: rgba(0, 0, 0, 0.1);
            padding: ${spacing.milli};
            border-radius: 4px;
            overflow-x: auto;
            margin: ${spacing.milli} 0;
          }
          & strong {
            font-weight: normal;
          }
          & em {
            font-style: normal;
          }
          & a {
            color: ${colors.petrolBlue};
            html[data-theme='dark'] & {
              color: ${colors.skyBlue};
            }
          }

          /* Table styles matching ChatPage */
          & .table-wrapper {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            margin: ${spacing.deci} 0;
            border-radius: 8px;
            border: 1px solid ${colors.cloud};
          }

          & table {
            border-collapse: collapse;
            width: 100%;
            display: table;
            font-size: 0.9em;
            margin: 0;
          }

          & th,
          & td {
            border: none;
            border-bottom: 1px solid ${colors.cloud};
            padding: ${spacing.deci};
            text-align: left;
            vertical-align: top;
            white-space: normal;
            word-break: normal;
            line-height: 1.5;
          }

          & th {
            background-color: rgba(0, 0, 0, 0.05);
            font-weight: 600;
            min-width: 120px;
            white-space: nowrap;
            border-bottom: 2px solid ${colors.cloud};
          }

          & td {
            white-space: pre-line;

            & br {
              display: block;
              content: '';
              margin-top: 0.5em;
            }

            & ul,
            & ol {
              margin: 0.5em 0;
              padding-left: 1.5em;
            }

            & li {
              margin: 0.25em 0;
            }

            & strong {
              font-weight: normal;
            }

            & em {
              font-style: normal;
            }
          }

          & tr:nth-of-type(even) {
            background-color: rgba(0, 0, 0, 0.02);
          }

          & tr:last-child td {
            border-bottom: none;
          }

          html[data-theme='dark'] & {
            & .table-wrapper {
              border-color: ${colors.tungsten};
            }

            & th,
            & td {
              border-color: ${colors.tungsten};
            }

            & th {
              background-color: rgba(255, 255, 255, 0.05);
              border-bottom-color: ${colors.tungsten};
            }

            & tr:nth-of-type(even) {
              background-color: rgba(255, 255, 255, 0.02);
            }
          }
        `}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            table: ({ children, ...props }) => (
              <div className="table-wrapper">
                <table {...props}>{children}</table>
              </div>
            ),
            td: ({ children, ...props }) => <td {...props}>{children}</td>,
            th: ({ children, ...props }) => <th {...props}>{children}</th>,
            strong: ({ children }) => <span>{children}</span>,
            em: ({ children }) => <span>{children}</span>,
          }}
        >
          {cleanMarkdownContent(analysis)}
        </ReactMarkdown>
      </div>
    );
  }

  // Fallback for simple content without markdown
  return (
    <div
      css={css`
        margin: 0;
      `}
    >
      {/* Trade Metrics */}
      {metrics && typeof metrics === 'object' && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-size: 12px;
              color: ${colors.silver};
              margin-bottom: ${spacing.centi};
            `}
          >
            Trading Metrics
          </div>
          {Object.entries(metrics)
            .slice(0, 4)
            .map(([key, value]: [string, any], index: number) => (
              <div
                key={index}
                css={css`
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: ${spacing.centi};
                  font-size: 14px;
                  line-height: 1.4;
                `}
              >
                <span
                  css={css`
                    color: ${colors.silver};
                  `}
                >
                  {key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </span>
                <span
                  css={css`
                    color: ${colors.liquorice};
                    font-weight: 500;

                    html[data-theme='dark'] & {
                      color: ${colors.sugar};
                    }
                  `}
                >
                  {typeof value === 'number' ? value.toLocaleString() : value}
                </span>
              </div>
            ))}
        </div>
      )}

      {/* Simple analysis content */}
      {analysis && (
        <div
          css={css`
            font-size: 14px;
            line-height: 1.4;
            color: ${colors.liquorice};

            html[data-theme='dark'] & {
              color: ${colors.sugar};
            }
          `}
        >
          {analysis}
        </div>
      )}

      {/* Recent Trades */}
      {trades && Array.isArray(trades) && trades.length > 0 && (
        <div
          css={css`
            margin-top: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-size: 12px;
              color: ${colors.silver};
              margin-bottom: ${spacing.centi};
            `}
          >
            Recent Trades ({trades.length})
          </div>
          {trades.slice(0, 3).map((trade: any, index: number) => (
            <div
              key={index}
              css={css`
                display: flex;
                justify-content: space-between;
                margin-bottom: ${spacing.centi};
                padding: ${spacing.centi};
                background: ${colors.fog};
                border-radius: 4px;

                html[data-theme='dark'] & {
                  background: var(--card-background);
                }
              `}
            >
              <span
                css={css`
                  color: ${colors.liquorice};
                  font-weight: 500;

                  html[data-theme='dark'] & {
                    color: ${colors.sugar};
                  }
                `}
              >
                {trade.symbol} {trade.side}
              </span>
              <span
                css={css`
                  color: ${colors.silver};
                  font-size: 12px;
                `}
              >
                {trade.volume ? `${trade.volume.toLocaleString()} shares` : 'Volume N/A'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
